import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Paper,
  Typography,
  TextField,
  Button,
  InputAdornment,
} from "@mui/material";
import LockIcon from "@mui/icons-material/Lock";
import { useLocation, useNavigate } from "react-router-dom";
import {
  ResendOtpService,
  VerifyOtpAPI,
} from "../../services/LoginService.jsx";
import { RoleListingService } from "../../services/UserOnboardingService";
import useGlobalState from "../../Context";
import { useLoading } from "../../common/LoadingContext";
import { useNotificationContext } from "../../common/NotificationContext";
import {
  setSignIn,
  setSpareAccessRights,
  setTechnicalDocAccessRights,
  setAdvisoriesAccessRights,
  setRollList,
} from "../../reducer/action";
import {
  roleAccessForSpare,
  roleAccessForTechnicalDoc,
  roleAccessForAdvisories,
} from "../../utils/RoleAccessByLoginUser";

const OTP_LENGTH = 6;

const Otp = () => {
  const [otp, setOtp] = useState(new Array(OTP_LENGTH).fill(""));
  const [timer, setTimer] = useState(60);
  const [canResend, setCanResend] = useState(false);

  const navigate = useNavigate();
  const inputRefs = useRef([]);
  const { state } = useLocation();

  const [, dispatch] = useGlobalState();
  const { showNotification } = useNotificationContext();
  const { setLoading } = useLoading();

  const email = state?.email || "";

  useEffect(() => {
    let interval;
    if (timer > 0) {
      interval = setInterval(() => setTimer((prev) => prev - 1), 1000);
    } else {
      setCanResend(true);
    }
    return () => clearInterval(interval);
  }, [timer]);

  const handleChange = (value, index) => {
    if (!/^[0-9]?$/.test(value)) return;

    const newOtp = [...otp];
    newOtp[index] = value;

    setOtp(newOtp);

    if (value && index < OTP_LENGTH - 1) {
      inputRefs.current[index + 1].focus();
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === "Backspace" && otp[index] === "" && index > 0) {
      inputRefs.current[index - 1].focus();
    }
  };

  const handlePaste = (e) => {
    const pasted = e.clipboardData.getData("text").trim();
    if (/^\d{6}$/.test(pasted)) {
      setOtp(pasted.split(""));
      inputRefs.current[OTP_LENGTH - 1].focus();
    }
  };

  const handleVerify = async (data) => {
    try {
      const finalOtp = otp.join("");
      if (finalOtp.length !== 6) {
        showNotification("Error", "Please enter complete OTP", "error");
        return;
      }

      if (!/^\d{6}$/.test(finalOtp)) {
        showNotification("Error", "OTP should contain only numbers", "error");
        return;
      }

      // setLoading(true);
      const param = {
        username: state?.email,
        otp: finalOtp,
      };
      // setLoading(true);

      const response = await VerifyOtpAPI(param);
      if (response.statusCode === 6) {
       
        dispatch(setSignIn(response.data));
        localStorage.setItem("partTypeListItem", "");
        const sparePermissionObj = roleAccessForSpare(response.data?.authorities);
        const technicalDocPermissionObj = roleAccessForTechnicalDoc(
          response.data?.authorities
        );
        const advisoriesPermissionObj = roleAccessForAdvisories(
          response.data?.authorities
        );
        dispatch(
          setSpareAccessRights({
            read: sparePermissionObj.readPermission,
            write: sparePermissionObj.writePermission,
            addDelete: sparePermissionObj.addDeletePermission,
          })
        );
        dispatch(
          setTechnicalDocAccessRights({
            read: technicalDocPermissionObj.readPermission,
            write: technicalDocPermissionObj.writePermission,
            addDelete: technicalDocPermissionObj.addDeletePermission,
          })
        );
        dispatch(
          setAdvisoriesAccessRights({
            read: advisoriesPermissionObj.readPermission,
            write: advisoriesPermissionObj.writePermission,
            addDelete: advisoriesPermissionObj.addDeletePermission,
          })
        );
        try {
          await fetchRoleList();
        } catch (err) {
          console.error("OTP Error:", err);
          showNotification(
            "Warning",
            "Authenticated successfully, but OTP could not be sent. Please try again.",
            "warning"
          );
        }
      } else {
        showNotification("Error", response.message, "error");
      }
    } catch (err) {
      console.log("err login", err);
      showNotification("Error", err.message ?? "Failed to fetch details");
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleList = async () => {
    try {
      const requestObject = {
        pageNumber: 0,
        pageSize: 200,
        filterSearch: null,
        active: 0,
        sortDirection: "ASC",
        sortBy: "name",
      };
      const roleListingResponse = await RoleListingService(requestObject);
      if (roleListingResponse.statusCode === 6) {
        let roleArrayList = roleListingResponse.data.content.map((value) => {
          return value.name;
        });
        dispatch(setRollList(roleArrayList));
        navigate("/notifications");
      } else {
        showNotification("Error", roleListingResponse.message, "error");
      }
    } catch (err) {
      console.log("error", err);
    } finally {
      // setLoading(false);
    }
  };

  const handleResend = async () => {
    try {
      setLoading(true);

      const requestObject = {
        username: state?.email,
      };
      const resendResp = await ResendOtpService(requestObject);

      if (resendResp?.statusCode === 6) {
        showNotification("Success", "OTP resent successfully!", "success");

        setTimer(60);
        setCanResend(false);
        setOtp(new Array(6).fill(""));
        inputRefs.current[0].focus();
      } else {
        showNotification(
          "Error",
          resendResp?.message || "Failed to resend OTP",
          "error"
        );
      }
    } catch (err) {
      console.error("Resend OTP error:", err);
      showNotification(
        "Error",
        "Failed to resend OTP. Please try again.",
        "error"
      );
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    navigate("/login");
  };

  return (
    <Box
      sx={{
        width: "100%",
        height: "100vh",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        background: "#f5f7fa",
      }}>
      <Paper
        elevation={4}
        sx={{
          p: 4,
          width: 400,
          borderRadius: 3,
          textAlign: "center",
        }}>
        <LockIcon sx={{ fontSize: 40, color: "#003f93" }} />
        <Typography variant="h6" sx={{ mt: 1 }}>
          OTP Verification
        </Typography>

        <Typography variant="body2" sx={{ mt: 1, color: "gray" }}>
          Enter the 6-digit code sent on Email/Mobile{" "}
          <b style={{ color: "#003f93" }}></b>
        </Typography>

        {/* OTP BOXES */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            mt: 3,
          }}
          onPaste={handlePaste}>
          {otp.map((digit, index) => (
            <TextField
              key={index}
              inputRef={(el) => (inputRefs.current[index] = el)}
              value={digit}
              onChange={(e) => handleChange(e.target.value, index)}
              onKeyDown={(e) => handleKeyDown(e, index)}
              inputProps={{
                maxLength: 1,
                style: {
                  textAlign: "center",
                  fontSize: "22px",
                  padding: "10px",
                },
              }}
              sx={{
                width: 45,
              }}
            />
          ))}
        </Box>

        <Typography sx={{ mt: 2, fontSize: 14, color: "gray" }}>
          {canResend ? (
            <span
              onClick={handleResend}
              style={{
                color: "#003f93",
                cursor: "pointer",
                fontWeight: "600",
              }}>
              Resend OTP
            </span>
          ) : (
            <>
              Resend OTP in <span style={{ color: "red" }}>{timer}s</span>
            </>
          )}
        </Typography>

        {/* Buttons */}
        <Box sx={{ mt: 3, display: "flex", justifyContent: "space-between" }}>
          <Button
            variant="outlined"
            sx={{ width: "45%" }}
            onClick={handleCancel}>
            Cancel
          </Button>

          <Button
            variant="contained"
            sx={{ width: "45%", backgroundColor: "#003f93" }}
            onClick={handleVerify}>
            Verify
          </Button>
        </Box>
      </Paper>
    </Box>
  );
};

export default Otp;
